import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { registerFcmTokenIfNative, isNativeAndroid } from "@/lib/androidBridge";
import { safeSetItem } from "@/lib/authStorage";
const AuthCtx = createContext(null);

/**
 * Auth persistence rules:
 *   - localStorage holds ONLY `shakthi_token` (the JWT). No user object.
 *   - React `user` state is populated on boot from `/auth/me`.
 *   - 401 → hard logout (interceptor already handles it).
 *   - 5xx / network / timeout → KEEP the token; pages retry via `sakthi:refocus`.
 *
 * Why no cached user? bank_staff docs embed `photo_b64` + `id_card_b64` — each
 * a full-size selfie/ID card. Two logins used to blow past the 5 MB
 * localStorage quota and wedge the whole app. Now every fresh boot loads the
 * user in a single network call.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isNativeAndroid() && !localStorage.getItem("shakthi_token")) {
      try {
        const raw = window.SakthiBridge.getAuth();
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.token) safeSetItem("shakthi_token", parsed.token);
      } catch { /* ignore malformed */ }
    }
    // Legacy cleanup — remove any oversized cached user from previous versions.
    try { localStorage.removeItem("shakthi_user"); localStorage.removeItem("shakthi_admin_user"); } catch { /* ignore */ }

    const token = localStorage.getItem("shakthi_token");
    if (!token) { setLoading(false); return; }

    api.get("/auth/me").then((r) => {
      setUser(r.data);
      if (isNativeAndroid()) registerFcmTokenIfNative();
    }).catch((err) => {
      if (err?.response?.status === 401) {
        try { localStorage.removeItem("shakthi_token"); } catch { /* ignore */ }
        if (isNativeAndroid()) { try { window.SakthiBridge.clearAuth(); } catch {} }
        setUser(null);
      }
    }).finally(() => setLoading(false));
  }, []);

  const login = (token, u) => {
    safeSetItem("shakthi_token", token);
    setUser(u);
    if (isNativeAndroid()) {
      try { window.SakthiBridge.saveAuth(token, ""); } catch {}
      registerFcmTokenIfNative();
    }
  };

  const logout = () => {
    localStorage.removeItem("shakthi_token");
    if (isNativeAndroid()) { try { window.SakthiBridge.clearAuth(); } catch {} }
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, login, logout }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
